import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Grape, ShoppingBag, Menu, Globe, User } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import CartSheet from "./CartSheet";

const Navbar = () => {
  const { t, lang, setLang, cartCount } = useApp();
  const { user } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/store", label: t("nav.store") },
    { to: "/wine-configurator", label: t("nav.wine") },
    { to: "/label-designer", label: t("nav.label") },
    { to: "/how-it-works", label: t("nav.how") },
    { to: "/producers", label: t("nav.producers") },
    { to: "/regions", label: t("nav.regions") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/contacts", label: t("nav.contacts") },
  ];

  const solid = scrolled || !isHome;
  const textColor = solid ? "text-ink" : "text-cream";

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${solid ? "bg-cream/95 backdrop-blur-md shadow-sm" : "bg-transparent"}`}>
      <nav className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src="/logo_moc.png" alt="My Own Cuvee" className={`h-11 w-auto transition-all duration-300 ${solid ? "" : "brightness-0 invert drop-shadow-[0_1px_8px_rgba(0,0,0,0.5)]"}`} />
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {links.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link key={l.to} to={l.to} className={`text-sm font-medium tracking-wide transition-colors relative group ${textColor} ${active ? "" : "opacity-80 hover:opacity-100"}`}>
                {l.label}
                <span className={`absolute -bottom-1.5 left-0 h-0.5 bg-gold transition-all duration-300 ${active ? "w-full" : "w-0 group-hover:w-full"}`} />
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <div className={`hidden sm:flex items-center rounded-full border ${solid ? "border-ink/15" : "border-cream/30"} overflow-hidden text-xs font-semibold`}>
            {["de", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`px-2.5 py-1 transition-colors ${lang === l ? "bg-wine text-white" : `${textColor} hover:bg-wine/10`}`}>{l.toUpperCase()}</button>
            ))}
          </div>

          <Link to={user ? "/account" : "/login"} data-testid="nav-account-link" title={user ? t("auth.account") : t("auth.loginCta")} className={`relative p-2 rounded-full transition-colors ${solid ? "text-ink hover:bg-wine/10" : "text-cream hover:bg-cream/10"}`}>
            <User className="w-5 h-5" />
            {user && <span className="absolute -top-0.5 -right-0.5 bg-gold w-2.5 h-2.5 rounded-full" />}
          </Link>

          <button data-testid="nav-cart-button" onClick={() => setCartOpen(true)} className={`relative p-2 rounded-full transition-colors ${solid ? "text-ink hover:bg-wine/10" : "text-cream hover:bg-cream/10"}`}>
            <ShoppingBag className="w-5 h-5" />
            {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-gold text-ink text-[10px] font-bold w-4.5 h-4.5 min-w-4 px-1 rounded-full flex items-center justify-center">{cartCount}</span>}
          </button>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button className={`lg:hidden p-2 rounded-full transition-colors ${solid ? "text-ink hover:bg-wine/10" : "text-cream hover:bg-cream/10"}`}><Menu className="w-6 h-6" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream w-72">
              <div className="mb-8 mt-2">
                <img src="/logo_moc.png" alt="My Own Cuvee" className="h-10 w-auto" />
              </div>
              <div className="flex flex-col gap-1">
                {links.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} className={`py-2.5 px-3 rounded-lg font-medium transition-colors ${location.pathname === l.to ? "bg-wine text-white" : "text-ink hover:bg-cream-dark"}`}>{l.label}</Link>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-6 px-3">
                <Globe className="w-4 h-4 text-ink/60" />
                {["de", "en"].map((l) => (
                  <button key={l} onClick={() => setLang(l)} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${lang === l ? "bg-wine text-white" : "bg-cream-dark text-ink"}`}>{l.toUpperCase()}</button>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      <CartSheet open={cartOpen} onOpenChange={setCartOpen} />
    </header>
  );
};

export default Navbar;
